<template>
  <div class="assistente">
    <!-- ================= cabeçalho / identidade (AV 26) ================= -->
    <div class="topo">
      <div class="avatar">
        <img v-if="avatarUrl" :src="avatarUrl" alt="" />
        <i v-else class="el-icon-service"></i>
      </div>
      <div class="titulo">
        <div class="nome">{{ identidade.nome || 'Assistente' }}</div>
        <div class="sub">Secretaria Municipal de Educação — Timbé do Sul</div>
      </div>
      <div class="acoes-topo">
        <el-tag size="mini" :type="tagNivel.tipo" data-test="tag-nivel">{{ tagNivel.texto }}</el-tag>
        <el-button v-if="sessaoId && !encerrado" type="text" size="mini"
                   @click="encerrarPeloBotao" data-test="btn-encerrar">encerrar</el-button>
      </div>
    </div>

    <!-- AV 1 — carimbo do modo simulado, inequívoco -->
    <el-alert v-if="govbr.simulado" type="warning" :closable="false" show-icon
              class="u-mb-5" data-test="aviso-govbr-simulado"
              title="Gov.br PENDENTE DE HOMOLOGAÇÃO"
              description="A autenticação Gov.br está pendente de abertura de protocolo oficial e criação de subdomínio oficial."></el-alert>

    <!-- ================= conversa ================= -->
    <div class="conversa" ref="conversa" data-test="conversa">
      <div v-for="(m, i) in mensagens" :key="i" :class="['balao', m.de]">
        <div v-if="m.titulo" class="balao-titulo">{{ m.titulo }}</div>
        <!--
          O corpo é quebrado em pedaços de texto e de link. Continua SEM `v-html`: cada
          pedaço é interpolado, e o link vira uma âncora de verdade em vez de texto solto
          que o cidadão teria de copiar à mão.
        -->
        <div class="balao-corpo"><template v-for="(p, j) in partes(m.corpo)"><a
            v-if="p.link" :key="j" :href="p.valor" target="_blank" rel="noopener noreferrer"
            class="balao-link" data-test="balao-link">{{ p.valor }}</a><template
            v-else>{{ p.valor }}</template></template></div>

        <!-- AV 10 — as opções mostram número E letra; digitar qualquer um funciona -->
        <div v-if="m.opcoes && m.opcoes.length" class="opcoes">
          <el-button v-for="o in m.opcoes" :key="o.indice" size="mini" plain
                     class="opcao" @click="responder(String(o.indice))"
                     data-test="opcao">
            {{ o.indice }}) {{ o.rotulo }} <span class="letra">ou "{{ o.letra }}"</span>
          </el-button>
        </div>
      </div>

      <div v-if="enviando" class="balao assistente-digitando" data-test="digitando">
        <i class="el-icon-loading"></i> digitando…
      </div>
    </div>

    <!-- ================= erro ================= -->
    <el-alert v-if="erro" type="error" :closable="true" show-icon class="u-mt-5"
              data-test="erro" :title="erro" @close="erro = null"></el-alert>

    <!-- ================= entrada ================= -->
    <div class="entrada">
      <el-input v-model="texto" :placeholder="placeholder" :disabled="encerrado || enviando"
                @keyup.enter.native="responder()" data-test="campo-texto">
        <el-button slot="append" icon="el-icon-s-promotion" :loading="enviando"
                   :disabled="encerrado" @click="responder()" data-test="btn-enviar"></el-button>
      </el-input>

      <div class="dicas">
        Digite o <b>número</b> ou a <b>letra</b> da opção. Para sair a qualquer momento,
        escreva <b>sair</b>; para voltar, <b>menu</b>.
      </div>
    </div>

    <!-- ================= encerrado ================= -->
    <div v-if="encerrado" class="encerrado" data-test="encerrado">
      <el-alert type="success" :closable="false" show-icon
                :title="'Atendimento encerrado' + (protocolo ? ' — protocolo ' + protocolo : '')">
        <div class="obs" v-if="duracaoTexto">Duração: {{ duracaoTexto }}</div>
      </el-alert>

      <!-- AV 9 — comprovante por e-mail -->
      <div class="u-mt-10">
        <el-input v-model="emailComprovante" placeholder="Seu e-mail para receber o comprovante"
                  size="small" style="max-width:340px" data-test="campo-email">
          <el-button slot="append" :loading="enviandoComprovante" @click="pedirComprovante"
                     data-test="btn-comprovante">ENVIAR</el-button>
        </el-input>
        <div v-if="comprovante" class="obs u-mt-5" data-test="resultado-comprovante">
          {{ comprovante.envio.mensagem }}
          <el-tag v-if="comprovante.reenvio" size="mini" class="u-ml-5">reenvio — mesmo protocolo</el-tag>
        </div>

        <!--
          AV 9 — a via que NÃO depende do servidor de e-mail do município.

          Sem SMTP configurado, o botão acima informa o protocolo e mais nada: o cidadão iria
          embora sem documento. Baixar o comprovante é a alternativa que está sob nosso
          controle, e vale também para quem simplesmente não tem e-mail.
        -->
        <div class="u-mt-5">
          <el-button type="text" size="mini" :loading="baixandoComprovante"
                     @click="baixarComprovante" data-test="btn-comprovante-pdf">
            <i class="el-icon-download"></i> baixar comprovante em PDF
          </el-button>
          <span class="obs u-ml-5">não depende de e-mail</span>
        </div>
      </div>

      <el-button type="primary" size="small" class="u-mt-10" @click="recomecar"
                 data-test="btn-recomecar">NOVO ATENDIMENTO</el-button>
    </div>
  </div>
</template>

<script>
/**
 * FASE 5 / AV-3 — atendimento pelo canal `site`.
 *
 * A tela é fina: manda texto, desenha o que voltou. Toda regra (navegação, validação,
 * verificação de identidade, ação) é do backend — as invariantes do AV-1 e AV-2 valem.
 *
 * O QUE ESTA TELA NÃO FAZ, e a auditoria verifica:
 *
 * · não decide para qual nó ir — o motor decide;
 * · não valida CPF/data por conta própria como se fosse autoridade (o backend revalida);
 * · não guarda nem envia nível de identidade — o servidor é quem sabe;
 * · não usa `v-html` — rótulo e corpo vêm do banco e são interpolados;
 * · não revela existência de aluno/boletim/fila: exibe exatamente a mensagem do servidor,
 *   que é genérica por desenho.
 */
import assistenteApi from '../assistenteApi'

export default {
  data () {
    return {
      api: null,
      identidade: {},
      govbr: { simulado: false },
      sessaoId: null,
      mensagens: [],
      texto: '',
      enviando: false,
      erro: null,
      encerrado: false,
      desambiguando: false,
      protocolo: null,
      iniciadoEm: null,
      encerradoEm: null,
      emailComprovante: '',
      comprovante: null,
      baixandoComprovante: false,
      enviandoComprovante: false,
      nivel: 'anonima'
    }
  },

  computed: {
    /*
     * A URL do avatar é montada AQUI, a partir da base da API.
     *
     * O servidor diz apenas SE existe avatar (booleano) — mandar a URL pronta faria a
     * imagem depender de `APP_URL` estar correta, e um APP_URL errado quebraria só o avatar,
     * silenciosamente. É a mesma decisão que o widget já tomava.
     */
    avatarUrl () {
      if (!this.identidade.avatar) return null
      return (this.$store.state.config.baseUrlApi || '') + '/api/publico/assistente/avatar'
    },

    placeholder () {
      if (this.encerrado) return 'Atendimento encerrado'
      return 'Digite sua mensagem…'
    },
    tagNivel () {
      const m = {
        anonima: { tipo: 'info', texto: 'não identificado' },
        identificada: { tipo: 'warning', texto: 'identificado' },
        verificada: { tipo: 'success', texto: 'identidade verificada' }
      }
      return m[this.nivel] || m.anonima
    },
    duracaoTexto () {
      if (!this.iniciadoEm || !this.encerradoEm) return null
      const s = Math.max(0, Math.round((this.encerradoEm - this.iniciadoEm) / 1000))
      const min = Math.floor(s / 60)
      return min > 0 ? min + ' min ' + (s % 60) + ' s' : s + ' s'
    }
  },

  mounted () {
    this.api = assistenteApi.criar({
      axios: this.$axios,
      base: this.base(),
      auth: this.auth()
    })
    this.carregarIdentidade()
    this.iniciar()
  },

  methods: {
    base () { return this.$store.state.config.baseUrlApi },
    auth () { return this.$store.getters.getHeaderAuth },

    /**
     * Divide o corpo em trechos de texto e de URL.
     *
     * A invariante da tela é não usar `v-html` — o corpo vem do banco e é interpolado. Por
     * isso a linkificação acontece AQUI, produzindo uma lista de pedaços que o template
     * desenha: texto continua sendo texto (escapado pelo Vue) e a URL vira `<a>`.
     */
    partes (corpo) {
      const texto = corpo === null || corpo === undefined ? '' : String(corpo)
      if (!texto) return []

      const re = /(https?:\/\/[^\s<>"']+)/g
      const out = []
      let ultimo = 0
      let m

      while ((m = re.exec(texto)) !== null) {
        if (m.index > ultimo) out.push({ link: false, valor: texto.slice(ultimo, m.index) })

        // Pontuação final não faz parte do endereço: "…/novatos." abriria com o ponto.
        let url = m[1]
        const sobra = url.match(/[.,;:)\]]+$/)
        if (sobra) url = url.slice(0, -sobra[0].length)

        out.push({ link: true, valor: url })
        if (sobra) out.push({ link: false, valor: sobra[0] })

        ultimo = m.index + m[1].length
      }

      if (ultimo < texto.length) out.push({ link: false, valor: texto.slice(ultimo) })

      return out
    },

    /** Mensagem de erro legível. O backend nega com 403 e corpo próprio. */
    textoErro (e) {
      const r = e && e.response ? e.response : null
      if (!r) return 'Não foi possível falar com o servidor.'
      if (r.data && typeof r.data.erro === 'string') return r.data.erro
      if (r.data && r.data.erro && r.data.erro.text) return r.data.erro.text
      if (r.status === 429) return 'Muitas mensagens em pouco tempo. Aguarde um instante.'
      return 'Não foi possível concluir. Tente novamente.'
    },

    carregarIdentidade () {
      const self = this
      this.api.identidade().then(function (r) { self.identidade = r.data || {} }).catch(function () {})
      this.api.govbrSituacao().then(function (r) { self.govbr = r.data || {} }).catch(function () {})
    },

    iniciar () {
      const self = this
      this.enviando = true
      this.erro = null

      const dados = {}
      if (this.$route.query.instituicao) dados.instituicao = this.$route.query.instituicao
      if (this.$route.query.origem) dados.origem = this.$route.query.origem
      if (this.$route.query.fluxo) dados.fluxo = this.$route.query.fluxo
      if (this.$route.query.email) dados.email = this.$route.query.email

      this.api.iniciar(dados).then(function (r) {
        self.enviando = false
        self.aplicar(r.data)
        self.iniciadoEm = Date.now()

        /*
         * AV 4 — DESAMBIGUAÇÃO.
         *
         * Dois alunos no mesmo telefone/e-mail: o servidor pergunta a data de nascimento
         * antes de identificar. Ele NUNCA escolhe um dos candidatos, e não diz quantos são —
         * a tela recebe só a pergunta.
         */
        if (r.data.desambiguacao && r.data.desambiguacao.aguardando) {
          self.desambiguando = true
          self.mensagens.push({ de: 'assistente', corpo: r.data.desambiguacao.pergunta })
          self.rolar()
        }
      }).catch(function (e) {
        self.enviando = false
        self.erro = self.textoErro(e)
      })
    },

    /** F5-FINAL-2 (F5) — envia a data de nascimento pedida pela desambiguação. */
    responderDesambiguacao (texto) {
      const self = this
      this.mensagens.push({ de: 'cidadao', corpo: texto })
      this.texto = ''
      this.enviando = true
      this.erro = null
      this.rolar()

      this.api.desambiguar(this.sessaoId, texto).then(function (r) {
        self.enviando = false
        self.desambiguando = !!r.data.aguardando

        if (r.data.mensagem) {
          self.mensagens.push({ de: 'assistente', corpo: r.data.mensagem })
        }
        self.rolar()
      }).catch(function (e) {
        self.enviando = false
        self.erro = self.textoErro(e)
      })
    },

    responder (valor) {
      if (this.enviando || this.encerrado) return          // duplo clique não duplica

      const texto = valor !== undefined ? valor : this.texto
      if (!String(texto).trim()) return

      // Enquanto a desambiguação estiver pendente, o que o cidadão digita é a DATA.
      if (this.desambiguando) return this.responderDesambiguacao(String(texto))

      const self = this
      this.mensagens.push({ de: 'cidadao', corpo: String(texto) })
      this.texto = ''
      this.enviando = true
      this.erro = null
      this.rolar()

      this.api.mensagem(this.sessaoId, texto).then(function (r) {
        self.enviando = false
        self.aplicar(r.data)
      }).catch(function (e) {
        self.enviando = false
        self.erro = self.textoErro(e)
      })
    },

    /** AV 7 — interrupção pela AÇÃO DA INTERFACE, além do comando textual. */
    encerrarPeloBotao () {
      if (this.enviando || this.encerrado) return
      const self = this
      this.enviando = true
      this.api.encerrar(this.sessaoId).then(function (r) {
        self.enviando = false
        self.aplicar(r.data)
      }).catch(function (e) {
        self.enviando = false
        self.erro = self.textoErro(e)
      })
    },

    /** Desenha a instrução do motor. Nenhuma decisão aqui. */
    aplicar (dados) {
      if (!dados) return

      if (dados.sessao_id) this.sessaoId = dados.sessao_id

      /*
       * F5-POC-1 — o que o motor falou NO CAMINHO vem antes.
       *
       * Uma resposta pode ser mais de um balão: o motor atravessa nós que falam (o boletim,
       * o calendário, uma informação) até parar num nó que espera resposta. Sem desenhar
       * `mensagens`, o conteúdo chegava do servidor e era descartado aqui — o cidadão via
       * apenas a pergunta seguinte, como se nada tivesse sido consultado.
       *
       * A ordem é a do servidor: a resposta antes da próxima pergunta.
       */
      const self = this
      ;(dados.mensagens || []).forEach(function (m) {
        if (!m || (!m.corpo && !m.titulo)) return
        self.mensagens.push({
          de: 'assistente', titulo: m.titulo || null, corpo: m.corpo || '', opcoes: []
        })
      })

      const msg = {
        de: 'assistente',
        titulo: dados.titulo || null,
        corpo: dados.corpo || '',
        opcoes: dados.opcoes || []
      }
      if (msg.corpo || msg.titulo || msg.opcoes.length) this.mensagens.push(msg)

      if (dados.tipo === 'encerramento' || dados.tipo === 'encerrada') {
        this.encerrado = true
        this.encerradoEm = Date.now()
        this.atualizarTrilha()
      }
      if (dados.tipo === 'transferencia') {
        this.encerrado = true
        this.encerradoEm = Date.now()
      }

      this.rolar()
    },

    /** Nível de identidade e protocolo vêm do SERVIDOR, nunca do estado local. */
    atualizarTrilha () {
      if (!this.sessaoId) return
      const self = this
      this.api.trilha(this.sessaoId).then(function (r) {
        const s = (r.data && r.data.sessao) || {}
        self.nivel = s.nivel_identidade || 'anonima'
        self.protocolo = s.protocolo || null
      }).catch(function () {})
    },

    /**
     * Baixa o comprovante em PDF.
     *
     * O arquivo vem do servidor como binário; o navegador precisa de um blob e de um clique
     * sintético para salvá-lo. `responseType: 'blob'` é obrigatório — sem ele o axios trata a
     * resposta como texto e o PDF chega corrompido.
     */
    baixarComprovante () {
      if (this.baixandoComprovante) return
      const self = this
      this.baixandoComprovante = true

      this.api.comprovantePdf(this.sessaoId).then(function (r) {
        const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }))
        /*
         * O nome do arquivo vem do SERVIDOR, que é quem sabe o protocolo emitido.
         *
         * A tela nem sempre tem o protocolo na mão — quem encerra pela palavra "sair" ainda
         * não pediu comprovante nenhum. Montar o nome aqui produzia
         * `comprovante-atendimento.pdf` para todos, e dois downloads se sobrescreviam na
         * pasta do cidadão.
         */
        const disposicao = (r.headers && (r.headers['content-disposition'] || '')) || ''
        const doServidor = (disposicao.match(/filename="?([^";]+)"?/) || [])[1]

        const a = document.createElement('a')
        a.href = url
        a.download = doServidor || ('comprovante-' + (self.protocolo || 'atendimento') + '.pdf')
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
        self.baixandoComprovante = false
      }).catch(function (e) {
        self.baixandoComprovante = false
        self.erro = self.textoErro(e)
      })
    },

    pedirComprovante () {
      if (this.enviandoComprovante) return          // reenvio é idempotente no servidor
      const self = this
      this.enviandoComprovante = true

      this.api.comprovante(this.sessaoId, this.emailComprovante).then(function (r) {
        self.enviandoComprovante = false
        if (r.data && r.data.erro) { self.erro = r.data.erro; return }
        self.comprovante = r.data
        self.protocolo = r.data.protocolo
      }).catch(function (e) {
        self.enviandoComprovante = false
        self.erro = self.textoErro(e)
      })
    },

    recomecar () {
      this.sessaoId = null
      this.mensagens = []
      this.encerrado = false
      this.protocolo = null
      this.comprovante = null
      this.emailComprovante = ''
      this.nivel = 'anonima'
      this.encerradoEm = null
      this.iniciar()
    },

    rolar () {
      const self = this
      this.$nextTick(function () {
        const el = self.$refs.conversa
        if (el) el.scrollTop = el.scrollHeight
      })
    }
  }
}
</script>

<style scoped>
.balao-link { color: #337ecc; text-decoration: underline; word-break: break-all; }
.balao-link:hover { color: #1f5c99; }
.assistente { max-width: 720px; margin: 0 auto; }
.topo { display: flex; align-items: center; gap: 10px; padding: 8px 0; border-bottom: 1px solid #ebeef5; }
.avatar { width: 38px; height: 38px; border-radius: 50%; background: #ecf5ff; display: flex;
          align-items: center; justify-content: center; overflow: hidden; }
.avatar img { width: 100%; height: 100%; object-fit: cover; }
.titulo { flex: 1; }
.nome { font-weight: 600; }
.sub { font-size: 11px; color: #909399; }
.acoes-topo { text-align: right; }
.conversa { height: 380px; overflow-y: auto; padding: 12px 4px; background: #fafafa; border-radius: 4px; }
.balao { max-width: 78%; margin-bottom: 10px; padding: 8px 10px; border-radius: 8px;
         font-size: 13px; white-space: pre-wrap; }
.balao.assistente { background: #fff; border: 1px solid #ebeef5; }
.balao.cidadao { background: #409EFF; color: #fff; margin-left: auto; }
.balao-titulo { font-weight: 600; margin-bottom: 4px; }
.assistente-digitando { color: #909399; font-size: 12px; border: none; background: none; }
.opcoes { margin-top: 8px; display: flex; flex-direction: column; gap: 4px; align-items: flex-start; }
.opcao { text-align: left; }
.letra { color: #909399; font-size: 10px; }
.entrada { margin-top: 10px; }
.dicas { font-size: 11px; color: #909399; margin-top: 4px; }
.encerrado { margin-top: 12px; }
.obs { font-size: 12px; color: #606266; }
</style>
